/** @jsxImportSource @emotion/react */
import * as color from 'style/color';
import React from 'react';
import cx from 'classnames';

interface ButtonProps extends React.ComponentProps<'button'> {
  variant: 'cta' | 'primary' | 'default';
  children?: React.ReactNode;
}

export function ButtonDivider() {
  return (
    <div
      aria-label="button-divider"
      css={{
        margin: '0 24px',
        backgroundColor: '#c6c7c8',
        width: 1,
        height: 43,
        opacity: 0.39,
      }}
    />
  );
}

export function ButtonGroup({
  direction = 'ltr',
  ...rest
}: {
  children?: React.ReactNode;
  direction?: 'rtl' | 'ltr';
}) {
  return (
    <div
      css={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: direction === 'ltr' ? 'row' : 'row-reverse',
        '& button': {
          margin: '0 10px',
          '&:first-of-type': {
            margin: direction === 'ltr' ? '0 10px 0 0' : '0 0 0 10px',
          },
          '&:last-of-type': {
            margin: direction === 'ltr' ? '0 0 0 10px' : '0 10px 0 0',
          },
        },
      }}
      {...rest}
    />
  );
}

export type IconButtonProps = Omit<ButtonProps, 'variant'>
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(({
  className,
  ...props
}, ref) => {
  return (
    <button
      ref={ref}
      className={cx(
        'text-[13px] bg-transparent px-[5px] py-[1px] leading-none border-0 outline-none',
        'flex items-center gap-1',
        props.disabled
          ? 'text-[#a7a7a7] hover:text-[#a7a7a7] cursor-default'
          : 'inherit hover:text-brand',
        className,
      )}
      {...props}
    />
  );
})

type VerticalIconButtonProps = {
  children?: React.ReactNode;
} & React.ComponentProps<'button'>;
export function VerticalIconButton({children, ...rest}: VerticalIconButtonProps) {
  return (
    <button
      css={{
        border: 'none',
        outline: 'none',
        background: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 11,
        color: rest.disabled ? '#a7a7a7' : 'inherit',
        '&:hover': {
          color: rest.disabled ? '#a7a7a7' : color.primary,
        },
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

export default function Button({variant, className, ...rest}: ButtonProps) {
  let fontColor = color.dark;
  let hoverColor = {
    color: color.primaryDark,
    backgroundColor: '#fff',
  };
  let borderColor = '#d5dadd';
  let backgroundColor = '#fff';
  switch (variant) {
    case 'cta':
      fontColor = '#fff';
      borderColor = color.primary;
      backgroundColor = color.primary;
      hoverColor = {
        color: '#fff',
        backgroundColor: color.primaryDark,
      };
      break;
    case 'primary':
      fontColor = color.primary;
      borderColor = color.primary;
      backgroundColor = '#fff';
      hoverColor = {
        color: color.primaryDark,
        backgroundColor: '#fff',
      };
      break;
  }

  return (
    <button
      className={cx(className, 'h-[35px]')}
      css={{
        padding: '1px 6px',
        fontSize: 16,
        borderRadius: 2,
        textAlign: 'center',
        border: `1px solid ${borderColor}`,
        minWidth: 188,
        color: fontColor,
        backgroundColor: backgroundColor,
        opacity: rest.disabled ? 0.5 : 1,
        lineHeight: 1,
        '&:hover': {
          color: hoverColor.color,
          backgroundColor: hoverColor.backgroundColor,
        },
      }}
      {...rest}
    />
  );
}
